<script setup lang="ts">
import { motion } from 'motion-v'
import type { IntRange } from 'type-fest'

const { t, tm, rt } = useI18n()

function getStaticAsset(index: StringifiedInt<IntRange<0, 3>>) {
  const value = match(index)
    .with('0', () => 'free-demo.jpg')
    .with('1', () => 'existing-customer.jpg')
    .with('2', () => 'partner-up.jpg')
    .exhaustive()

  return `${S3_ASSETS_URL}/contact/${value}`
}

function getInquiryType(index: StringifiedInt<IntRange<0, 3>>) {
  return match(index)
    .with('0', () => 'free-demo' as const)
    .with('1', () => 'existing-customer' as const)
    .with('2', () => 'partner-up' as const)
    .exhaustive()
}
</script>

<template>
  <section class="mx-auto max-w-screen-xl px-4 py-5 lg:px-8">
    <UiSectionHeadline
      :title="t('contact_us.title')"
      :subtitle="t('contact_us.subtitle')"
    />

    <motion.div
      class="mx-auto mt-10 grid max-w-screen-lg grid-cols-1 gap-5 md:grid-cols-3"
      :variants="{
        initial: { opacity: 0 },
        animate: { opacity: 1 }
      }"
      initial="initial"
      while-in-view="animate"
      exit="initial"
    >
      <motion.div
        v-for="(item, itemIndex) in tm('contact_us.items')"
        :key="itemIndex"
        :variants="{
          initial: { y: 50 },
          animate: {
            y: 0,
            transition: {
              type: 'spring',
              stiffness: 100,
              damping: 10,
              delay: Number(itemIndex) * 0.1
            }
          }
        }"
        initial="initial"
        while-in-view="animate"
        exit="initial"
        class="group relative grid grid-flow-col overflow-clip rounded-3xl border border-neutral-200 bg-white md:grid-flow-row md:grid-rows-[auto_1fr]"
      >
        <div class="relative w-full select-none">
          <UiImageWithOverlay
            role="presentation"
            :src="getStaticAsset(itemIndex)"
            class="size-full object-cover"
            container-class="rounded-3xl overflow-clip size-full aspect-[12/16] md:aspect-square"
          />

          <i18n-t
            :keypath="`contact_us.items.${itemIndex}.title`"
            tag="p"
            class="absolute inset-0 flex flex-col justify-end text-balance p-2 text-[0.7rem] text-white md:items-center md:p-5 md:text-base"
          >
            <template #title_placeholder>
              <span class="text-[0.9rem] font-medium leading-4 md:text-xl md:leading-5">
                {{ rt(item.title_placeholder) }}
              </span>
            </template>
          </i18n-t>
        </div>

        <div class="flex grow flex-col justify-between gap-y-5 p-2 px-3 text-left text-sm md:p-5 md:text-center md:text-base">
          <!-- `whitespace-pre-line` is used because there's an `\n` being used inside of `en-US.json` for a quick fix -->
          <i18n-t
            :keypath="`contact_us.items.${itemIndex}.description`"
            tag="p"
            class="whitespace-pre-line text-gray-600"
          >
            <template #description_placeholder>
              <strong class="font-bold">
                {{ rt(item.description_placeholder) }}
              </strong>
            </template>
          </i18n-t>

          <UiButton
            :color="itemIndex === '0' ? 'accent' : 'neutral'"
            class="w-full"
          >
            {{ rt(item.action) }}

            <UiIcon
              name="arrow-forward-rounded"
              class="hidden transition-transform duration-200 group-hover:translate-x-1 md:visible md:block"
            />
          </UiButton>
        </div>

        <RouterLink
          :to="{
            name: `/contact-us/${getInquiryType(itemIndex)}`,
          }"
          class="absolute inset-0"
        />
      </motion.div>
    </motion.div>
  </section>
</template>
